import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
    StyledRangeSlider,
    StyledSlider,
    StyledSliderRange,
    StyledSliderTrack,
    StyledSliderMinThumb,
    StyledSliderMaxThumb,
} from './styled';

export type RangeDataType = {
    min: number;
    max: number;
};

export type RangeProps = {
    min: number;
    max: number;
    step?: number;
    value?: RangeDataType;
    onChange: (value: RangeDataType) => void;
    disabled?: boolean;
};

export const RangeSlider = ({ min, max, onChange, step = 1, value, disabled }: RangeProps) => {
    const [minVal, setMinVal] = useState(value?.min || min);
    const [maxVal, setMaxVal] = useState(value?.max || max);

    const minValRef = useRef<HTMLInputElement>(null);
    const maxValRef = useRef<HTMLInputElement>(null);
    const range = useRef<HTMLDivElement>(null);

    /*
     * Convert value to percentage to know the position
     */
    const getPercent = useCallback(
        (value: number) => {
            return Math.round(((value - min) / (max - min)) * 100);
        },
        [min, max]
    );

    /*
     * Adjust the left position of the selected range track
     */
    useEffect(() => {
        if (maxValRef.current) {
            const minPercent = getPercent(value?.min || minVal);
            const maxPercent = getPercent(+maxValRef.current.value);

            if (range.current) {
                range.current.style.left = `${minPercent}%`; // 4px margin so that the track dont extend before the thumb
                range.current.style.width = `${maxPercent - minPercent}%`;
            }
        }
    }, [minVal, value?.min, getPercent]);

    /*
     * Adjust the right position of the selected range track
     */
    useEffect(() => {
        if (minValRef.current) {
            const minPercent = getPercent(+minValRef.current.value);
            const maxPercent = getPercent(value?.max || maxVal);

            if (range.current) {
                range.current.style.width = `${maxPercent - minPercent}%`;
            }
        }
    }, [maxVal, value?.max, getPercent]);

    return (
        <StyledRangeSlider>
            <StyledSliderMinThumb
                type="range"
                step={step}
                min={min}
                max={max}
                value={value?.min || minVal || 0}
                ref={minValRef}
                disabled={disabled}
                name="min"
                aria-label="min"
                onChange={(event) => {
                    const inputValue = Math.min(
                        +event.target.valueAsNumber,
                        (value?.max || maxVal) - 1
                    );
                    onChange({ min: inputValue, max: value?.max || maxVal });
                    setMinVal(inputValue);
                }}
            />
            <StyledSliderMaxThumb
                type="range"
                step={step}
                min={min}
                max={max}
                disabled={disabled}
                name="max"
                aria-label="max"
                value={value?.max || maxVal || 0}
                ref={maxValRef}
                onChange={(event) => {
                    const inputValue = Math.max(
                        event.target.valueAsNumber,
                        (value?.min || minVal) + 1
                    );
                    onChange({ min: value?.min || minVal, max: inputValue });
                    setMaxVal(inputValue);
                }}
            />
            <StyledSlider>
                <StyledSliderTrack disabled={disabled} />
                <StyledSliderRange disabled={disabled} ref={range} />
            </StyledSlider>
        </StyledRangeSlider>
    );
};
